import { Clock, CheckSquare, FolderKanban, FileText, Heart } from 'lucide-react';
import { useState } from 'react';
import { SearchableFilter } from './SearchableFilter';

interface Activity {
  id: string;
  user: {
    name: string;
    avatar?: string;
    initials: string;
  };
  action: string;
  target: string;
  timestamp: string;
  type: 'time' | 'task' | 'project' | 'team' | 'document' | 'timesheet';
  projectName?: string;
  weekLabel?: string;
  duration?: string;
  loved: boolean;
  loveCount: number;
}

// Sample activity data - in production this would come from backend
const sampleActivities: Activity[] = [
  {
    id: '1',
    user: {
      name: 'Current User',
      initials: 'CU',
    },
    action: 'submitted timesheet',
    target: '8h 30m',
    timestamp: '2 minutes ago',
    type: 'timesheet',
    projectName: 'Website Redesign',
    weekLabel: 'w05.26',
    duration: '8h 30m',
    loved: false,
    loveCount: 2,
  },
  {
    id: '2',
    user: {
      name: 'Team Member',
      initials: 'TM',
    },
    action: 'logged',
    target: '5h 45m on UI Design',
    timestamp: '15 minutes ago',
    type: 'timesheet',
    projectName: 'Mobile App Development',
    weekLabel: 'w05.26',
    duration: '5h 45m',
    loved: true,
    loveCount: 5,
  },
  {
    id: '3',
    user: {
      name: 'Alex Chen',
      initials: 'AC',
    },
    action: 'completed task',
    target: 'UI Design Review',
    timestamp: '30 minutes ago',
    type: 'task',
    projectName: 'Website Redesign',
    weekLabel: 'w05.26',
    loved: false,
    loveCount: 3,
  },
  {
    id: '4',
    user: {
      name: 'Sarah Johnson',
      initials: 'SJ',
    },
    action: 'submitted timesheet',
    target: '7h 15m',
    timestamp: '1 hour ago',
    type: 'timesheet',
    projectName: 'Brand Strategy',
    weekLabel: 'w05.26',
    duration: '7h 15m',
    loved: false,
    loveCount: 1,
  },
];

export function ActivityLog() {
  const [selectedWeek, setSelectedWeek] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [activities, setActivities] = useState(sampleActivities);

  // Get unique weeks and projects for filters
  const weeks = Array.from(new Set(activities.map(a => a.weekLabel).filter(Boolean))) as string[];
  const projects = Array.from(new Set(activities.map(a => a.projectName).filter(Boolean))) as string[];

  // Filter activities based on selected filters
  const filteredActivities = activities.filter(activity => {
    const weekMatch = selectedWeek === 'all' || activity.weekLabel === selectedWeek;
    const projectMatch = selectedProject === 'all' || activity.projectName === selectedProject;
    return weekMatch && projectMatch;
  });

  const toggleLove = (id: string) => {
    setActivities((prevActivities) =>
      prevActivities.map((activity) =>
        activity.id === id
          ? {
              ...activity,
              loved: !activity.loved,
              loveCount: activity.loved ? activity.loveCount - 1 : activity.loveCount + 1,
            }
          : activity
      )
    );
  };

  const getActivityIcon = (type: Activity['type']) => {
    switch (type) {
      case 'time':
      case 'timesheet':
        return <Clock className="w-4 h-4 text-[#e30840]" />;
      case 'task':
        return <CheckSquare className="w-4 h-4 text-[#4A90E2]" />;
      case 'project':
        return <FolderKanban className="w-4 h-4 text-[#27AE60]" />;
      case 'document':
        return <FileText className="w-4 h-4 text-[#9B59B6]" />;
      default:
        return <Clock className="w-4 h-4 text-[#808080]" />;
    }
  };

  return (
    <div className="bg-white rounded-md shadow-[0px_2px_15px_-3px_rgba(0,0,0,0.07),0px_10px_20px_-2px_rgba(0,0,0,0.04)] p-6 h-[220px] flex flex-col">
      {/* Header with filters */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-[#808080] uppercase tracking-wide">Activity Log</p>
        <div className="flex items-center gap-2">
          <SearchableFilter
            options={weeks}
            value={selectedWeek}
            onChange={setSelectedWeek}
            placeholder="Week"
            allLabel="All Weeks"
          />
          <SearchableFilter
            options={projects}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="Project"
            allLabel="All Projects"
          />
        </div>
      </div>

      {/* Activity List */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {filteredActivities.length > 0 ? (
          filteredActivities.map((activity) => (
            <div
              key={activity.id}
              className="flex items-start gap-3 py-2 border-b border-[#f5f5f5] last:border-0"
            >
              {/* User Avatar */}
              <div className="w-8 h-8 rounded-full bg-[#f6f6f6] flex items-center justify-center flex-shrink-0">
                {activity.user.avatar ? (
                  <img
                    src={activity.user.avatar}
                    alt={activity.user.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="text-[10px] font-medium text-[#808080]">
                    {activity.user.initials}
                  </span>
                )}
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {getActivityIcon(activity.type)}
                    <p className="text-[12px] text-[#262626]">
                      <span className="font-medium">{activity.user.name}</span>{' '}
                      <span className="text-[#808080]">{activity.action}</span>{' '}
                      <span className="font-medium">{activity.target}</span>
                    </p>
                  </div>
                  
                  {/* Love button */}
                  <button
                    onClick={() => toggleLove(activity.id)}
                    className={`flex items-center gap-1 text-[10px] ${
                      activity.loved ? 'text-[#e30840]' : 'text-[#808080]'
                    } hover:text-[#e30840] transition-colors`}
                  >
                    <Heart
                      className={`w-3 h-3 ${activity.loved ? 'fill-current' : ''}`}
                    />
                    {activity.loveCount > 0 && <span>{activity.loveCount}</span>}
                  </button>
                </div>

                <div className="flex items-center gap-2 mt-1">
                  {activity.projectName && (
                    <span className="text-[10px] text-[#e30840]">{activity.projectName}</span>
                  )}
                  <span className="text-[10px] text-[#b3b3b3]">{activity.timestamp}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-full text-[12px] text-[#808080]">
            No activity found
          </div>
        )}
      </div>
    </div>
  );
}
